import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Drawer, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { Archive, Clock, FileCheck, MoreVertical, Phone, TrendingUp, X } from "lucide-react";
import sh from "bundles/common/utils/sh";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";
import UserChartsSection from "./user_charts/UserChartsSection";

const OUTCOME_LABELS = {
  no_answer: "No answer",
  follow_up_today: "Callback",
  follow_up_later: "Callback",
  signed_up: "Signed up",
  applied: "Applied",
  voicemail: "Voicemail",
  not_interested: "Not interested",
  to_be_checked: "To be checked",
  sign_up_later: "Sign up later",
  email_sent: "Email sent",
};

const OUTCOME_COLORS = {
  signed_up: "hsl(142, 71%, 45%)",
  follow_up_today: "hsl(217, 91%, 55%)",
  follow_up_later: "hsl(217, 91%, 55%)",
  no_answer: "hsl(0, 72%, 51%)",
  voicemail: "hsl(38, 92%, 50%)",
  applied: "hsl(150, 38%, 17%)",
  not_interested: "hsl(150, 10%, 46%)",
  to_be_checked: "hsl(150, 10%, 46%)",
  sign_up_later: "hsl(262, 65%, 55%)",
  email_sent: "hsl(217, 91%, 55%)",
};

const BAR_COLORS = {
  no_answer: "hsl(0, 72%, 51%)",
  voicemail: "hsl(38, 92%, 50%)",
  follow_up: "hsl(217, 91%, 55%)",
  sign_up_later: "hsl(262, 65%, 55%)",
  signed_up: "hsl(142, 71%, 45%)",
  applied: "hsl(150, 38%, 17%)",
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return "";
  const diffMs = new Date() - new Date(dateString);
  if (diffMs < 0) return "just now";
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

const outcomeColor = (outcome) => OUTCOME_COLORS[outcome] || "hsl(150, 10%, 46%)";
const outcomeLabel = (outcome) => OUTCOME_LABELS[outcome] || outcome;

const fontBold = "Segoe UI Semibold, sans-serif, system-ui";
const fontNormal = "Segoe UI Regular, sans-serif, system-ui";

const InternalUserDrawer = ({
  selectedUserId,
  onClose,
  period,
  startDate = null,
  endDate = null,
  onArchived,
  endpointBase = "/internal/coordinator",
  allowArchive = true,
}) => {
  const open = Boolean(selectedUserId);
  const userId = selectedUserId;
  const currentUser = useSelector((state) => state.current_internal_user);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const canArchive =
    allowArchive &&
    (currentUser?.role === "admin" || currentUser?.role === "coordinator") &&
    !!userId && currentUser?.id !== userId;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setError("");
    setData(null);

    const params = { period };
    if (period === "custom" && startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    }

    sh.get(`${endpointBase}/internal_users/${userId}`, { params })
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load recruiter details."))
      .finally(() => setLoading(false));
  }, [open, userId, period, startDate, endDate, endpointBase]);

  const handleArchive = () => {
    if (!userId || archiving) return;
    setArchiving(true);
    sh.post(`/internal/coordinator/internal_users/${userId}/archive`, {
      authenticity_token: authenticityToken,
    })
      .then(() => {
        setConfirmingArchive(false);
        onArchived?.(userId);
        onClose?.();
      })
      .catch(() => {
        setConfirmingArchive(false);
      })
      .finally(() => setArchiving(false));
  };

  const user = data?.internal_user;
  const perf = data?.performance;
  const outcomes = data?.outcomes_breakdown;
  const activity = data?.recent_activity || [];

  const lastActivityTime = activity.length > 0 ? activity[0].created_at : null;

  const barRows = [
    { key: "no_answer", label: "No answer", color: BAR_COLORS.no_answer },
    { key: "voicemail", label: "Voicemail", color: BAR_COLORS.voicemail },
    { key: "follow_up", label: "Callbacks", color: BAR_COLORS.follow_up },
    { key: "sign_up_later", label: "Sign up later", color: BAR_COLORS.sign_up_later },
    { key: "signed_up", label: "Signed up", color: BAR_COLORS.signed_up },
    { key: "applied", label: "Applied", color: BAR_COLORS.applied },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          maxWidth: "100%",
          borderLeft: "1px solid hsl(140, 8%, 88%)",
          backgroundColor: "#ffffff",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,.25)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflowY: "auto" }}>
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, py: 10 }}>
            <CircularProgress size={32} sx={{ color: "hsl(150, 38%, 17%)" }} />
          </Box>
        )}

        {error && !loading && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, py: 10, px: 3 }}>
            <Typography sx={{ fontSize: 13, fontFamily: fontNormal, color: "hsl(0, 72%, 51%)", textAlign: "center" }}>
              {error}
            </Typography>
          </Box>
        )}

        {data && !loading && (
          <>
            {/* Header */}
            <Box sx={{ px: 2.5, py: 2.5, borderBottom: "1px solid hsl(140, 8%, 88%)", display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
              <UserAvatar user={user} size={48} tooltip={false} sx={{ fontSize: 16, fontFamily: fontBold }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 18, fontFamily: fontBold, color: "hsl(150, 30%, 12%)", lineHeight: 1.3 }}>
                  {user?.full_name}
                </Typography>
                <Typography sx={{ fontSize: 12, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)", mt: 0.25 }}>
                  Last active: {lastActivityTime ? formatRelativeTime(lastActivityTime) : "No activity"}
                </Typography>
              </Box>
              <IconButton onClick={onClose} sx={{ position: "absolute", top: 12, right: 12, color: "hsl(150, 10%, 46%)" }}>
                <X size={18} />
              </IconButton>
              {canArchive && (
                <>
                  <IconButton
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    sx={{ position: "absolute", top: 12, right: 44, color: "hsl(150, 10%, 46%)" }}
                  >
                    <MoreVertical size={18} />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    PaperProps={{
                      elevation: 2,
                      sx: {
                        borderRadius: "10px",
                        border: "1px solid hsl(140, 8%, 88%)",
                        minWidth: 160,
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => { setMenuAnchor(null); setConfirmingArchive(true); }}
                      sx={{
                        fontSize: 14,
                        fontFamily: fontNormal,
                        color: "hsl(0, 60%, 45%)",
                        gap: 1.5,
                        px: 1.5,
                        py: 0.75,
                        "&:hover": { backgroundColor: "hsl(0, 60%, 97%)" },
                      }}
                    >
                      <Archive size={15} />
                      Archive
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>

            {/* Performance */}
            <Box sx={{ px: 2.5, py: 2.5, borderBottom: "1px solid hsl(140, 8%, 88%)" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
                <StatBox icon={<Phone size={12} />} label="Calls made" value={perf?.calls_made ?? 0} color="hsl(150, 30%, 12%)" />
                <StatBox icon={<TrendingUp size={12} />} label="Connect rate" value={`${perf?.connect_rate ?? 0}%`} color="hsl(142, 71%, 45%)" />
                <StatBox icon={<FileCheck size={12} />} label="Sign-up rate" value={`${perf?.sign_rate ?? 0}%`} color="hsl(150, 38%, 17%)" />
              </Box>
            </Box>

            {/* Outcomes */}
            <Box sx={{ px: 2.5, py: 2.5, borderBottom: "1px solid hsl(140, 8%, 88%)" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {barRows.map((row) => {
                  const val = outcomes?.[row.key] ?? 0;
                  const total = perf?.calls_made || 1;
                  const pct = Math.min((val / total) * 100, 100);
                  return (
                    <Box key={row.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 12, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)", width: 80, flexShrink: 0 }}>
                        {row.label}
                      </Typography>
                      <Box sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: "hsl(140, 8%, 90%)", overflow: "hidden" }}>
                        <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 3, backgroundColor: row.color, transition: "width 0.4s ease" }} />
                      </Box>
                      <Typography sx={{ fontSize: 12, fontFamily: fontBold, color: "hsl(150, 30%, 12%)", width: 24, textAlign: "right", flexShrink: 0 }}>
                        {val}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Call history */}
            {activity.length > 0 && (
              <Box sx={{ px: 2.5, py: 2.5, borderBottom: "1px solid hsl(140, 8%, 88%)" }}>
                <Typography sx={{ fontSize: 11, fontFamily: fontBold, textTransform: "uppercase", letterSpacing: "0.06em", color: "hsl(150, 10%, 46%)", mb: 1.5 }}>
                  Call history
                </Typography>
                <Box>
                  {activity.slice(0, 6).map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1,
                        borderBottom: "1px solid hsl(140, 8%, 88%)",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Clock size={12} style={{ flexShrink: 0, color: "hsl(150, 10%, 46%)" }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12, fontFamily: fontBold, color: "hsl(150, 30%, 12%)" }} noWrap>
                          {entry.external_profile?.full_name}
                        </Typography>
                        <Typography sx={{ fontSize: 10, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)" }} noWrap>
                          {entry.external_profile?.desired_title}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 11, fontFamily: fontBold, color: outcomeColor(entry.outcome), flexShrink: 0 }}>
                        {outcomeLabel(entry.outcome)}
                      </Typography>
                      <Typography sx={{ fontSize: 10, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)", flexShrink: 0 }}>
                        {formatRelativeTime(entry.created_at)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Recent activity */}
            {activity.length > 0 && (
              <Box sx={{ px: 2.5, py: 2.5, borderBottom: "1px solid hsl(140, 8%, 88%)" }}>
                <Typography sx={{ fontSize: 11, fontFamily: fontBold, textTransform: "uppercase", letterSpacing: "0.06em", color: "hsl(150, 10%, 46%)", mb: 1.5 }}>
                  Recent activity
                </Typography>
                <Box>
                  {activity.slice(0, 4).map((entry) => (
                    <Box
                      key={`act-${entry.id}`}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        py: 1,
                        borderBottom: "1px solid hsl(140, 8%, 88%)",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)", lineHeight: 1.4 }} noWrap>
                          <Typography component="span" sx={{ fontSize: 12, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)" }}>Logged </Typography>
                          <Typography component="span" sx={{ fontSize: 12, fontFamily: fontBold, color: outcomeColor(entry.outcome) }}>{outcomeLabel(entry.outcome)}</Typography>
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 11, fontFamily: fontBold, color: "hsl(150, 30%, 12%)", lineHeight: 1.4, flexShrink: 0, maxWidth: "55%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {entry.external_profile?.full_name}
                          </Typography>
                          {entry.profile_pipeline?.title && (
                            <Typography sx={{ fontSize: 10, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              · {entry.profile_pipeline.title}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: 10, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)", flexShrink: 0, mt: "2px" }}>
                        {formatRelativeTime(entry.created_at)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Performance charts */}
            <Box sx={{ px: 2.5, py: 2.5 }}>
              <UserChartsSection
                userId={userId}
                period={period}
                startDate={startDate}
                endDate={endDate}
                columns={1}
                chartHeight={200}
              />
            </Box>
          </>
        )}
      </Box>
      <ConfirmDialog
        open={confirmingArchive}
        onClose={() => { if (!archiving) setConfirmingArchive(false); }}
        onConfirm={handleArchive}
        title="Archive recruiter?"
        description={`${user?.full_name || "This user"}'s data stays but they won't be able to sign in or appear in lists.`}
        confirmLabel={archiving ? "Archiving…" : "Archive"}
        cancelLabel="Cancel"
        destructive
      />
    </Drawer>
  );
};

const StatBox = ({ icon, label, value, color }) => (
  <Box
    sx={{
      backgroundColor: "hsl(140, 8%, 90%)",
      borderRadius: 2,
      p: 1.5,
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "hsl(150, 10%, 46%)" }}>
      {icon}
      <Typography sx={{ fontSize: 10, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)" }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 16, fontFamily: fontBold, color, lineHeight: 1 }}>
      {value}
    </Typography>
  </Box>
);

export default InternalUserDrawer;
